import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { Checkbox } from '../components/ui/Checkbox'
import { PageShell } from '../components/layout/PageShell'
import { PageHeader } from '../components/layout/PageHeader'
import { UploadZone } from '../components/upload/UploadZone'
import { ProcessingCard } from '../components/upload/ProcessingCard'
import { DocumentCard } from '../components/upload/DocumentCard'
import { AutoFillField } from '../components/fields/AutoFillField'
import { Button } from '../components/ui/Button'
import { StartOverButton } from '../components/ui/StartOverButton'
import { BackButton } from '../components/ui/BackButton'
import { SaveIndicator } from '../components/ui/SaveIndicator'
import { Badge } from '../components/ui/Badge'
import { SectionTopAlert } from '../components/ui/SectionTopAlert'
import { ManualFallbackDisclaimer } from '../components/ui/ManualFallbackDisclaimer'
import { useFormStore } from '../store/formStore'
import { scrollToFirstError, FIRST_INVALID_FIELD } from '../lib/scrollToError'
import { useSession } from '../apply/session'
import { processDocument, type ConfidenceMap, type DocAIExtraction } from '../api/docai'
import type { UploadedFileInfo } from '../api/types'

type SlotState = 'idle' | 'processing' | 'done' | 'error'

const UPLOAD_SUBTEXT = 'JPEG, PNG, PDF · Max 10MB · Max 50 pages (PDF) · 🔒 Encrypted & Private'

const UPLOAD_ERROR_TEXT =
  "We couldn't read this document. Try uploading it again, or fill in the fields below manually."

// Section 1 — Certificate of Incorporation / Registration fields. The `name`
// is both the form_data key and the mapper registry formKey (api/docai.ts) —
// auto-fill matches extracted values to these fields by name.
const SECTION1_FIELDS = [
  {
    name: 'legalCompanyName',
    label: 'Legal company name',
    placeholder: 'Full legal name',
    required: true,
  },
  {
    name: 'countryOfIncorporation',
    label: 'Country of incorporation',
    placeholder: 'Country',
    required: true,
  },
  {
    name: 'incorporationDate',
    label: 'Date of incorporation',
    placeholder: 'dd.mm.yyyy',
    required: true,
  },
  {
    name: 'registrationNumber',
    label: 'Registration number',
    placeholder: 'Registration number',
    required: true,
  },
] as const

const SECTION1_FIELD_NAMES = SECTION1_FIELDS.map((f) => f.name) as unknown as string[]

// Section 2 — Company license fields (auto-fill from the license document)
const SECTION2_FIELDS = [
  {
    // Key stays `licenseType` (used across the store, form_data, the DocAI
    // extraction map, and the SF mapping to License_Name_DocAI__c); only the
    // visible label changed to "License name" to match Salesforce and avoid
    // confusion with the regulator picklist Types_of_License__c.
    name: 'licenseType',
    label: 'License name',
    placeholder: 'e.g. EMI, PSP',
  },
  {
    name: 'licenseNumber',
    label: 'License number',
    placeholder: 'License number',
  },
] as const

const SECTION2_FIELD_NAMES = SECTION2_FIELDS.map((f) => f.name) as unknown as string[]

const DATE_FIELDS = ['incorporationDate']

interface DocSectionProps {
  title: string
  required: boolean
  helperText?: React.ReactNode
  children: React.ReactNode
}
function DocSection({ title, required, helperText, children }: DocSectionProps) {
  return (
    <section
      style={{
        background: 'var(--color-surface-container-lowest)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--spacing-8)',
        marginBottom: 'var(--spacing-8)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-3)',
          flexWrap: 'wrap',
          marginBottom: helperText ? 'var(--spacing-2)' : 'var(--spacing-8)',
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-title-md)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--color-text-primary)',
            margin: 0,
            lineHeight: 'var(--leading-tight)',
          }}
        >
          {title}
        </h2>
        <Badge variant={required ? 'required' : 'optional'} size="md">
          {required ? 'Required' : 'Optional'}
        </Badge>
      </div>
      {helperText && (
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--color-text-secondary)',
            margin: '0 0 var(--spacing-8) 0',
            lineHeight: 'var(--leading-normal)',
          }}
        >
          {helperText}
        </p>
      )}
      {children}
    </section>
  )
}

export function CompanyDetailsScreen() {
  const navigate = useNavigate()
  const store = useFormStore()
  const { documents, refreshDocuments } = useSession()

  // Per-field AI confidence, persisted in form_data so the badges survive
  // reloads and the Review screen can render the same values.
  const confidenceByField = (store.formData.confidenceByField as ConfidenceMap) ?? {}

  function mergeConfidence(incoming: ConfidenceMap) {
    store.setField('confidenceByField', { ...confidenceByField, ...incoming })
  }

  function clearConfidence(keys: string[]) {
    const next = { ...confidenceByField }
    for (const key of keys) delete next[key]
    store.setField('confidenceByField', next)
  }

  // ─── Section 1: Certificate of Incorporation / Registration ────────────────
  const [certState, setCertState] = useState<SlotState>('idle')
  const [certFile, setCertFile] = useState<UploadedFileInfo | null>(null)
  const [certTask, setCertTask] = useState<Promise<DocAIExtraction> | null>(null)
  const [shimmerActive, setShimmerActive] = useState(false)
  const [section1Values, setSection1Values] = useState<Record<string, string>>({
    legalCompanyName: (store.formData.legalCompanyName as string) || '',
    countryOfIncorporation: (store.formData.countryOfIncorporation as string) || '',
    incorporationDate: (store.formData.incorporationDate as string) || '',
    registrationNumber: (store.formData.registrationNumber as string) || '',
  })

  // ─── Registered office (moved here from Company Structure) ────────────────
  // Neither is its own extracted field in the Certificate of Incorporation's
  // SF template (which returns Legal Company Name / Registration Number /
  // Date of Incorporation / Country of Incorporation only), so both live
  // outside SECTION1_FIELDS/section1Values: they must not be cleared when the
  // certificate is replaced (clearSection1Fields), nor counted in the
  // "AI extracted N fields" badge on the document card. Registered office
  // country is still treated as AI-filled (see handleCertComplete) — it
  // takes Country of Incorporation's own value and confidence — but the
  // address has no source field on this document at all, so it stays manual.
  const [registeredOfficeAddress, setRegisteredOfficeAddress] = useState<string>(
    (store.formData.registeredOfficeAddress as string) || ''
  )
  const [registeredAddressCountry, setRegisteredAddressCountry] = useState<string>(
    (store.formData.registeredAddressCountry as string) || ''
  )

  function handleRegisteredOfficeAddressChange(val: string) {
    dismissErrorsOnEdit()
    setRegisteredOfficeAddress(val)
    store.setField('registeredOfficeAddress', val)
  }

  function handleRegisteredAddressCountryChange(val: string) {
    dismissErrorsOnEdit()
    setRegisteredAddressCountry(val)
    store.setField('registeredAddressCountry', val)
  }

  // ─── Section 2: License ─────────────────────────────────────────────────────
  const [licenseState, setLicenseState] = useState<SlotState>('idle')
  const [licenseFile, setLicenseFile] = useState<UploadedFileInfo | null>(null)
  const [licenseTask, setLicenseTask] = useState<Promise<DocAIExtraction> | null>(null)
  const [licenseShimmer, setLicenseShimmer] = useState(false)
  const [section2Values, setSection2Values] = useState<Record<string, string>>({
    licenseType: (store.formData.licenseType as string) || store.licenseType || '',
    licenseNumber: (store.formData.licenseNumber as string) || store.licenseNumber || '',
  })
  const [noLicence, setNoLicence] = useState<boolean>(
    (store.formData.noLicence as boolean) ?? false
  )

  // One-shot slot rehydration: a document persisted server-side marks its
  // slot done after a reload (the extracted values are already in form_data).
  const rehydratedRef = useRef(false)
  useEffect(() => {
    if (rehydratedRef.current || documents.length === 0) return
    rehydratedRef.current = true
    const cert = documents.find((d) => d.slot === 'certificate_of_incorporation')
    if (cert) {
      setCertFile((prev) => prev ?? { name: cert.filename, size: cert.size_bytes })
      setCertState((prev) => (prev === 'idle' ? 'done' : prev))
    }
    const license = documents.find((d) => d.slot === 'license')
    if (license) {
      setLicenseFile((prev) => prev ?? { name: license.filename, size: license.size_bytes })
      setLicenseState((prev) => (prev === 'idle' ? 'done' : prev))
    }
  }, [documents])

  // ─── Section 1 handlers ────────────────────────────────────────────────────
  // Clears the certificate fields (state + store + auto-fill flags) so the AI
  // badges and values don't linger when the document is removed or swapped.
  function clearSection1Fields() {
    const cleared: Record<string, string> = {
      legalCompanyName: '',
      countryOfIncorporation: '',
      incorporationDate: '',
      registrationNumber: '',
    }
    setSection1Values(cleared)
    Object.entries(cleared).forEach(([k, v]) => store.setField(k, v))
    // Registered office country isn't in SECTION1_FIELDS (see the note in
    // handleCertComplete), but it's re-derived from Country of incorporation
    // on every upload the same way the other fields are, so it clears here
    // too — a re-upload should reset it, not leave a stale AI value behind.
    setRegisteredAddressCountry('')
    store.setField('registeredAddressCountry', '')
    const clearedNames = [...SECTION1_FIELD_NAMES, 'registeredAddressCountry']
    store.setAutoFilled(
      store.autoFilledFields.filter((f) => !clearedNames.includes(f))
    )
    clearConfidence(clearedNames)
  }

  function handleCertFile(file: File) {
    // Replacing a processed document: drop the previous extraction first so the
    // fields fall back to their locked/empty state (no stale badge) while the
    // new document is read, then re-fill on completion.
    clearSection1Fields()
    setCertFile(file)
    setCertTask(processDocument(file, 'certificate_of_incorporation'))
    setCertState('processing')
    store.setUploadState('certificate', 'processing')
    store.markManualFallbackSeen()
  }

  function handleCertComplete(result: DocAIExtraction) {
    // Fill only what the extraction actually returned; the rest stays empty
    // and editable so the applicant can complete it manually.
    const extracted = SECTION1_FIELDS.filter((f) => result.fields[f.name] !== undefined)
    setSection1Values((prev) => {
      const next = { ...prev }
      for (const field of extracted) next[field.name] = result.fields[field.name]
      return next
    })
    extracted.forEach((f) => store.setField(f.name, result.fields[f.name]))
    const newlyAutoFilled: string[] = extracted.map((f) => f.name)
    // Registered office country isn't its own field in this document's SF
    // template (see the field-mapping note above SECTION1_FIELDS), but it
    // takes the same value as Country of incorporation — a real AI fill
    // (badge + confidence, mirrored from that field's own confidence), not a
    // plain default. Re-uploading the certificate overwrites it exactly like
    // every other section-1 field (clearSection1Fields resets it to '' first,
    // same as the others). Computed here — not inside a separate
    // setAutoFilled call — so both this field and the section1Values ones
    // land in ONE store.setAutoFilled call below; two sequential calls would
    // each read the same stale `store.autoFilledFields` closure and the
    // second would silently drop the first's additions.
    const incorporationCountry = result.fields.countryOfIncorporation
    const countryConfidence = result.confidence.countryOfIncorporation
    if (incorporationCountry !== undefined) {
      setRegisteredAddressCountry(incorporationCountry)
      store.setField('registeredAddressCountry', incorporationCountry)
      newlyAutoFilled.push('registeredAddressCountry')
    }
    store.setAutoFilled([...new Set([...store.autoFilledFields, ...newlyAutoFilled])])
    mergeConfidence({
      ...result.confidence,
      ...(incorporationCountry !== undefined && countryConfidence != null
        ? { registeredAddressCountry: countryConfidence }
        : {}),
    })
    setShimmerActive(true)
    setTimeout(() => setShimmerActive(false), 2000)
    setCertState('done')
    store.setUploadState('certificate', 'success')
    // Sync session's document list so a later "Edit" back to this step (a
    // fresh mount) sees the upload already there instead of re-prompting.
    void refreshDocuments()
  }

  function handleCertError(err: unknown) {
    console.error('[docai] certificate extraction failed', err)
    setCertState('error')
    setCertTask(null)
    store.setUploadState('certificate', 'error')
  }

  function resetCert() {
    setCertState('idle')
    setCertFile(null)
    setCertTask(null)
    store.setUploadState('certificate', 'idle')
    // MAF 29-05: removing the document erases the fields it pre-filled; they
    // re-lock (inactive) until a new document is uploaded.
    clearSection1Fields()
  }

  // Editing (or clearing) any field dismisses visible validation errors; they
  // only return if the user tries to Continue again. Consistent across steps.
  function dismissErrorsOnEdit() {
    setSubmitAttempted(false)
    setShowLicenseFieldErrors(false)
  }

  function handleSection1Field(name: string, val: string) {
    dismissErrorsOnEdit()
    setSection1Values((prev) => ({ ...prev, [name]: val }))
    store.setField(name, val)
  }

  // ─── Section 2 handlers ────────────────────────────────────────────────────
  // Clears the license fields (state + store + auto-fill flags) so the AI
  // badges and values don't linger when the document is removed or swapped.
  function clearSection2Fields() {
    const cleared: Record<string, string> = {
      licenseType: '',
      licenseNumber: '',
    }
    setSection2Values(cleared)
    Object.entries(cleared).forEach(([k, v]) => store.setField(k, v))
    store.setAutoFilled(
      store.autoFilledFields.filter((f) => !SECTION2_FIELD_NAMES.includes(f))
    )
    clearConfidence(SECTION2_FIELD_NAMES)
  }

  function handleLicenseFile(file: File) {
    // Replacing a processed document: drop the previous extraction first so the
    // fields fall back to empty (no stale badge) while the new document is read.
    clearSection2Fields()
    setLicenseFile(file)
    setLicenseTask(processDocument(file, 'license'))
    setLicenseState('processing')
    store.setUploadState('license', 'processing')
  }

  function handleLicenseComplete(result: DocAIExtraction) {
    const extracted = SECTION2_FIELDS.filter((f) => result.fields[f.name] !== undefined)
    setSection2Values((prev) => {
      const next = { ...prev }
      for (const field of extracted) next[field.name] = result.fields[field.name]
      return next
    })
    extracted.forEach((f) => store.setField(f.name, result.fields[f.name]))
    store.setAutoFilled([
      ...new Set([...store.autoFilledFields, ...extracted.map((f) => f.name)]),
    ])
    mergeConfidence(result.confidence)
    setLicenseShimmer(true)
    setTimeout(() => setLicenseShimmer(false), 2000)
    setLicenseState('done')
    store.setUploadState('license', 'success')
    store.setField('licenceFileUploaded', true)
    void refreshDocuments()
  }

  function handleLicenseError(err: unknown) {
    console.error('[docai] license extraction failed', err)
    setLicenseState('error')
    setLicenseTask(null)
    store.setUploadState('license', 'error')
  }

  function handleSection2Field(name: string, val: string) {
    dismissErrorsOnEdit()
    setSection2Values((prev) => ({ ...prev, [name]: val }))
    store.setField(name, val)
  }

  function resetLicense() {
    setLicenseState('idle')
    setLicenseFile(null)
    setLicenseTask(null)
    store.setUploadState('license', 'idle')
    store.setField('licenceFileUploaded', false)
    // Removing the document erases the fields it pre-filled.
    clearSection2Fields()
  }

  // MAF 26-05: surfaces the missing-requirement message only after a submit try.
  const [submitAttempted, setSubmitAttempted] = useState(false)
  // Per-field "X is required" messages on the manual license fields appear only
  // at a Continue checkpoint (not live while the user is typing the other field),
  // so we don't nag about a field they haven't reached yet.
  const [showLicenseFieldErrors, setShowLicenseFieldErrors] = useState(false)
  const certUploadRef = useRef<HTMLDivElement>(null)
  const licenseMsgRef = useRef<HTMLDivElement>(null)

  // ─── Continue gate ─────────────────────────────────────────────────────────
  // Section 1 (incorporation) gates the button.
  // MAF 29-05: the Certificate of Incorporation is a mandatory document. Its
  // fields are locked until DocAI processes the upload, then editable. When
  // extraction fails they unlock anyway — the manual-fallback path.
  const certLocked = certState !== 'done' && certState !== 'error'
  // Every required company-detail field must hold a value. The certificate
  // auto-fills these, but a user can clear one afterwards — so we still gate on
  // it and surface a per-field error under any that are left empty.
  const section1Filled = SECTION1_FIELDS.every(
    (f) => !f.required || (section1Values[f.name] ?? '').trim() !== ''
  )
  // A failed extraction still counts as a resolved certificate once the
  // fields are completed manually (the document itself was uploaded).
  const certResolved = certState === 'done' || (certState === 'error' && section1Filled)
  // Registered office country is never certificate-locked (it isn't part of
  // that extraction — see the note above SECTION1_FIELDS), so it gates
  // independently of certResolved/section1Filled. The address line itself is
  // optional (product change) — only the country is required.
  const officeAddressFilled = registeredAddressCountry.trim() !== ''
  // The license section needs an explicit answer: a processed document, both
  // fields filled manually, or the "does not hold a licence" checkbox.
  const licenseManuallyFilled =
    (section2Values.licenseType ?? '').trim() !== '' &&
    (section2Values.licenseNumber ?? '').trim() !== ''
  const licenseResolved = noLicence || licenseState === 'done' || licenseManuallyFilled
  // The Continue button stays active. Clicking it while something's missing
  // flips submitAttempted on — which reveals the document list below and a
  // per-field error on each empty detail — and we don't advance.
  const detailsReady = certResolved && section1Filled && officeAddressFilled && licenseResolved

  const showCertError = submitAttempted && certState === 'idle'
  // None of the three license paths chosen → highlight the whole section red.
  const showLicenseError = submitAttempted && !licenseResolved
  // Once the user types into either manual field, treat the manual path as
  // chosen: drop the error from the checkbox and upload zone (they're not using
  // those), and only flag the manual field(s) still left empty.
  const manualEntryStarted =
    (section2Values.licenseType ?? '').trim() !== '' ||
    (section2Values.licenseNumber ?? '').trim() !== ''
  const showLicensePathError = showLicenseError && !manualEntryStarted

  function handleContinue() {
    if (!detailsReady) {
      setSubmitAttempted(true)
      // Only now (a Continue with the manual path under way) do we surface the
      // per-field "required" messages on whichever manual field is still empty.
      if (manualEntryStarted) setShowLicenseFieldErrors(true)
      // Top to bottom: the certificate upload if it's still outstanding (it
      // gates everything below it), else the topmost invalid field, else the
      // licence section's message.
      scrollToFirstError(
        certState === 'idle' || certState === 'processing' ? certUploadRef.current : null,
        FIRST_INVALID_FIELD,
        licenseMsgRef.current
      )
      return
    }
    store.setStep(3)
    navigate('/company-structure')
  }

  return (
    <PageShell showStepper currentStep={1}>
      {/* Page heading + intro */}
      <PageHeader
        title="Company Details"
        intro="Let's get your company's official information!"
      />

      {/* ───────────────────────────────────────────────────────────────────
          SECTION 1 — Certificate of Incorporation / Registration (Required)
      ─────────────────────────────────────────────────────────────────── */}
      <DocSection
        title="Certificate of Incorporation or Registration"
        required
        helperText="Upload your certificate of incorporation or registration and our AI will fill in the company details below for you."
      >
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontWeight: 'var(--weight-semibold)',
            fontSize: 'var(--text-label-md)',
            color: 'var(--color-text-primary)',
            margin: '0 0 var(--spacing-2) 0',
          }}
        >
          Certificate document
        </p>
        <AnimatePresence mode="wait">
          {certState === 'idle' && (
            <motion.div
              key="cert-idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div ref={certUploadRef}>
                <UploadZone
                  onFileAccepted={handleCertFile}
                  subtext={UPLOAD_SUBTEXT}
                  required_error={showCertError}
                />
                {showCertError && (
                  <p
                    role="alert"
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-label-sm)',
                      color: 'var(--color-error-text)',
                      margin: 'var(--spacing-2) 0 0 var(--spacing-3)',
                    }}
                  >
                    Required: please upload to continue
                  </p>
                )}
              </div>
            </motion.div>
          )}
          {certState === 'processing' && certFile && certTask && (
            <motion.div
              key="cert-processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <ProcessingCard
                filename={certFile.name}
                task={certTask}
                onComplete={handleCertComplete}
                onError={handleCertError}
              />
            </motion.div>
          )}
          {certState === 'error' && (
            <motion.div
              key="cert-error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <UploadZone onFileAccepted={handleCertFile} subtext={UPLOAD_SUBTEXT} required_error />
              <p
                role="alert"
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-label-sm)',
                  color: 'var(--color-error-text)',
                  margin: 'var(--spacing-2) 0 var(--spacing-4) var(--spacing-3)',
                }}
              >
                {UPLOAD_ERROR_TEXT}
              </p>
              <ManualFallbackDisclaimer variant="inline" />
            </motion.div>
          )}
          {certState === 'done' && certFile && (
            <motion.div
              key="cert-done"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <DocumentCard
                file={certFile}
                fieldCount={SECTION1_FIELDS.length}
                onReplace={handleCertFile}
                onRemove={resetCert}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-form-field-gap)',
            marginTop: 'var(--spacing-8)',
          }}
        >
          {SECTION1_FIELDS.map((field, index) => {
            const isAutoFilled = store.autoFilledFields.includes(field.name)
            // Show a required-field error only once the user has tried to
            // continue and the field (editable, i.e. the certificate is in)
            // is left empty.
            const showError =
              submitAttempted &&
              field.required &&
              !certLocked &&
              (section1Values[field.name] ?? '').trim() === ''
            return (
              <AutoFillField
                key={field.name}
                label={field.label}
                name={field.name}
                type={DATE_FIELDS.includes(field.name) ? 'date' : 'text'}
                required={field.required}
                disabled={certLocked}
                value={section1Values[field.name] ?? ''}
                placeholder={field.placeholder}
                isAutoFilled={isAutoFilled}
                triggerShimmer={shimmerActive && isAutoFilled}
                shimmerDelay={index * 120}
                confidence={isAutoFilled ? confidenceByField[field.name] : undefined}
                error={showError ? `${field.label} is required` : undefined}
                onChange={(val) => handleSection1Field(field.name, val)}
              />
            )
          })}

          {/* Registered office — moved here from Company Structure, never
              locked/disabled and never cleared by a certificate re-upload.
              Country isn't its own field in this document's SF template, but
              takes the same value as Country of incorporation (AI-filled,
              with that field's own confidence) once the certificate
              resolves — see handleCertComplete. Address is always manual;
              this document has no street/city data to offer. */}
          <AutoFillField
            label="Registered office country"
            name="registeredAddressCountry"
            type="text"
            required
            value={registeredAddressCountry}
            placeholder="Country"
            isAutoFilled={store.autoFilledFields.includes('registeredAddressCountry')}
            triggerShimmer={shimmerActive && store.autoFilledFields.includes('registeredAddressCountry')}
            confidence={
              store.autoFilledFields.includes('registeredAddressCountry')
                ? confidenceByField.registeredAddressCountry
                : undefined
            }
            error={
              submitAttempted && registeredAddressCountry.trim() === ''
                ? 'Registered office country is required'
                : undefined
            }
            onChange={handleRegisteredAddressCountryChange}
          />
          <AutoFillField
            label="Registered office address"
            name="registeredOfficeAddress"
            type="text"
            value={registeredOfficeAddress}
            placeholder="City and street address"
            onChange={handleRegisteredOfficeAddressChange}
          />
        </div>
      </DocSection>

      {/* ───────────────────────────────────────────────────────────────────
          SECTION 2 — License (Optional)
      ─────────────────────────────────────────────────────────────────── */}
      <DocSection
        title="Company license"
        required={false}
        helperText="Upload your company license if your business has one, and our AI will fill in the details below from it. You can also complete this section manually."
      >
        {submitAttempted && !licenseResolved && (
          <SectionTopAlert ref={licenseMsgRef}>
            Complete one of the options below to continue
          </SectionTopAlert>
        )}
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            cursor: 'pointer',
            marginBottom: noLicence ? 0 : 'var(--spacing-8)',
          }}
        >
          <Checkbox
            checked={noLicence}
            error={showLicensePathError}
            onChange={(checked) => {
              dismissErrorsOnEdit()
              setNoLicence(checked)
              store.setField('noLicence', checked)
              if (checked && licenseState !== 'idle') {
                resetLicense()
              }
            }}
          />
          <span
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-body-sm)',
              color: 'var(--color-text-secondary)',
            }}
          >
            Our company does not hold a licence
          </span>
        </label>

        <AnimatePresence>
          {!noLicence && (
            <motion.div
              key="license-section"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <p
                style={{
                  fontFamily: 'var(--font-body)',
                  fontWeight: 'var(--weight-semibold)',
                  fontSize: 'var(--text-label-md)',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 var(--spacing-2) 0',
                }}
              >
                License document
              </p>

              <AnimatePresence mode="wait">
                {licenseState === 'idle' && (
                  <motion.div
                    key="license-idle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <UploadZone
                      onFileAccepted={handleLicenseFile}
                      subtext={UPLOAD_SUBTEXT}
                      required_error={showLicensePathError}
                    />
                  </motion.div>
                )}
                {licenseState === 'processing' && licenseFile && licenseTask && (
                  <motion.div
                    key="license-processing"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <ProcessingCard
                      filename={licenseFile.name}
                      task={licenseTask}
                      onComplete={handleLicenseComplete}
                      onError={handleLicenseError}
                    />
                  </motion.div>
                )}
                {licenseState === 'error' && (
                  <motion.div
                    key="license-error"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <UploadZone onFileAccepted={handleLicenseFile} subtext={UPLOAD_SUBTEXT} />
                    <p
                      role="alert"
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-label-sm)',
                        color: 'var(--color-error-text)',
                        margin: 'var(--spacing-2) 0 0 var(--spacing-3)',
                      }}
                    >
                      {UPLOAD_ERROR_TEXT}
                    </p>
                  </motion.div>
                )}
                {licenseState === 'done' && licenseFile && (
                  <motion.div
                    key="license-done"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <DocumentCard
                      file={licenseFile}
                      fieldCount={SECTION2_FIELDS.length}
                      onReplace={handleLicenseFile}
                      onRemove={resetLicense}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--spacing-form-field-gap)',
                  marginTop: 'var(--spacing-8)',
                }}
              >
                {SECTION2_FIELDS.map((field, index) => {
                  const isAutoFilled = store.autoFilledFields.includes(field.name)
                  const fieldEmpty = (section2Values[field.name] ?? '').trim() === ''
                  const gatesManualPath =
                    field.name === 'licenseType' || field.name === 'licenseNumber'
                  return (
                    <AutoFillField
                      key={field.name}
                      label={field.label}
                      name={field.name}
                      required={false}
                      // The "X is required" message appears only after a Continue
                      // checkpoint (showLicenseFieldErrors) — not live as the user
                      // types the other field — so we don't nag about a field they
                      // haven't reached. Before that, fields are border-only (the
                      // top banner carries the message).
                      error={
                        gatesManualPath && showLicenseFieldErrors && manualEntryStarted && fieldEmpty
                          ? `${field.label} is required`
                          : undefined
                      }
                      invalid={gatesManualPath && showLicenseError && !manualEntryStarted && fieldEmpty}
                      type={DATE_FIELDS.includes(field.name) ? 'date' : 'text'}
                      value={section2Values[field.name] ?? ''}
                      placeholder={field.placeholder}
                      isAutoFilled={isAutoFilled}
                      triggerShimmer={licenseShimmer && isAutoFilled}
                      shimmerDelay={index * 120}
                      confidence={
                        isAutoFilled ? confidenceByField[field.name] : undefined
                      }
                      onChange={(val) => handleSection2Field(field.name, val)}
                    />
                  )
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DocSection>

      {/* ─── CTA ────────────────────────────────────────────────────────── */}
      {/* Pulled up into the card's bottom margin so the required-fields note
          hugs the card above, with clear separation before the buttons. */}
      <div style={{ marginTop: 'calc(-1 * var(--spacing-4))' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 'var(--spacing-4)',
            minHeight: '20px',
            marginBottom: 'var(--spacing-10)',
          }}
        >
          <p
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-label-sm)',
              color: 'var(--color-text-tertiary)',
              margin: 0,
            }}
          >
            Fields marked with{' '}
            <span style={{ color: 'var(--color-action-primary)' }}>*</span> are required.
          </p>
          <SaveIndicator state={store.saveState} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-3)' }}>
          <BackButton to="/welcome" />
          <Button
            variant="primary"
            onClick={handleContinue}
          >
            Continue to Company Structure
          </Button>
          <StartOverButton inline />
        </div>
      </div>
    </PageShell>
  )
}
